import * as yup from "yup";
import {AncillaryEntityRequest, AncillaryEntityResponse, FormAncillaryModel, TaxCodeObject} from "../../../api/types/ancillary";
import {SelectElementModelBE, SelectElementModelFE} from "../../../api/types/generic";
import {fromGenericBeToGenericFe, fromGenericFeToGenericBe, i18nConvertNumberWithDecimals} from "../../../utils";
import {yupPickupFromFunc, yupPickupToFunc, yupReservationFromFunc, yupReservationToFunc} from "../../../yupUtils";
import dayjs from "dayjs";
import {maxNumberYupLimit} from "../../../constants";
import {convertToCents} from "../../../../../util/state/commonFunctions";

export const initialAncillaryForm = (legalEntityOptions: SelectElementModelFE[], dealerOption: SelectElementModelFE[]) => ({
    note: null,
    reservationFrom: "",
    reservationTo: "",
    pickupFrom: "",
    pickupTo: "",
    tpUnit: null,
    currency: "EUR",
    minimumAmount: null,
    maximumAmount: null,
    chargingWay: "",
    maxUnits: null,
    chargeType: "",
    nameAncillary: "",
    code: "",
    description: "",
    type: "",
    exceptionListPack: null,
    subtype: "",
    segments: [],
    cardinality: null,
    sellDesk: false,
    passageToCounterNeeded: true,
    applicationTiming: null,
    toleranceTime: null,
    // RF-02 (US-SYS-004) - 5 flag ancillary
    flagResellableAtDeskPostRa: false,
    flagResellableInSubstitution: false,
    flagDiscountApplicable: false,
    timeToChange: null,
    timeToRemove: null,
    additionalField: null,
    tax: false,
    taxCode: "",
    dealer: dealerOption.length === 1 ? dealerOption[0] : null,
    legalEntity: legalEntityOptions.length === 1 ? legalEntityOptions[0] : null
});

export const getAncillaryBaseDefinitionValidationSchema = (t: Function, ancillary: AncillaryEntityResponse | null) => {
    const yupReservationFrom = yupReservationFromFunc({
        t,
        id: ancillary?.id || null,
        reservationFrom: ancillary?.reservationFrom as string
    });
    const yupReservationTo = yupReservationToFunc({
        t,
        id: ancillary?.id || null,
        reservationTo: ancillary?.reservationTo as string,
        reservationFromFieldName: "reservationFrom"
    });
    const yupPickupFrom = yupPickupFromFunc({
        t,
        id: ancillary?.id || null,
        pickupFrom: ancillary?.pickupFrom as string
    });
    const yupPickupTo = yupPickupToFunc({
        t,
        id: ancillary?.id || null,
        reservationTo: ancillary?.reservationTo as string,
        pickupFrom: ancillary?.pickupFrom as string,
        pickupTo: ancillary?.pickupTo as string,
        pickupFromFieldName: "pickupFrom"
    });

    return yup.object().shape({
        dealer: yup
            .object()
            .shape({
                code: yup.string(),
                label: yup.string()
            })
            .nullable()
            .required(t("translation:validation.fieldMandatory")),
        legalEntity: yup
            .object()
            .shape({
                code: yup.string(),
                label: yup.string()
            })
            .nullable()
            .required(t("translation:validation.fieldMandatory")),
        segments: yup
            .array()
            .min(1, t("translation:validation.fieldMandatory"))
            .of(
                yup
                    .object()
                    .shape({
                        code: yup.string(),
                        label: yup.string()
                    })
                    .nullable()
                    .required(t("translation:validation.fieldMandatory"))
            ),
        code: yup
            .string()
            .nullable()
            .required(t("translation:validation.fieldMandatory")),
        nameAncillary: yup
            .string()
            .nullable()
            .required(t("translation:validation.fieldMandatory")),
        description: yup
            .string()
            .nullable()
            .required(t("translation:validation.fieldMandatory")),
        type: yup
            .string()
            .nullable()
            .required(t("translation:validation.fieldMandatory")),
        subtype: yup
            .string()
            .nullable()
            .required(t("translation:validation.fieldMandatory")),
        toleranceTime: yup
            .number()
            .nullable()
            .integer()
            .min(0, t("pricing:validation.positiveNumber"))
            .max(maxNumberYupLimit, t("pricing:validation.numberLowerThan") + maxNumberYupLimit),
        timeToChange: yup
            .number()
            .nullable()
            .integer()
            .min(0, t("pricing:validation.positiveNumber"))
            .max(maxNumberYupLimit, t("pricing:validation.numberLowerThan") + maxNumberYupLimit),
        timeToRemove: yup
            .number()
            .nullable()
            .integer()
            .min(0, t("pricing:validation.positiveNumber"))
            .max(maxNumberYupLimit, t("pricing:validation.numberLowerThan") + maxNumberYupLimit),
        cardinality: yup
            .number()
            .nullable()
            .min(0, t("pricing:validation.positiveNumber"))
            .integer()
            .required(t("translation:validation.fieldMandatory"))
            .max(maxNumberYupLimit, t("pricing:validation.numberLowerThan") + maxNumberYupLimit),
        tpUnit: yup.string()
            .nullable()
            .required(t("translation:validation.fieldMandatory")),
        reservationFrom: yupReservationFrom,
        reservationTo: yupReservationTo,
        pickupFrom: yupPickupFrom,
        pickupTo: yupPickupTo,
        currency: yup
            .string()
            .nullable()
            .required(t("translation:validation.fieldMandatory")),
        minimumAmount: yup.number()
            .nullable()
            .min(0, t("pricing:validation.positiveNumber"))
            .transform((o, v) => v ? convertToCents(v) : v)
            .max(maxNumberYupLimit, t("pricing:validation.numberLowerThan") + maxNumberYupLimit),
        maximumAmount: yup.number()
            .nullable()
            .transform((o, v) => v ? convertToCents(v) : v)
            .min(yup.ref("minimumAmount"), ({min}) => t("pricing:validation.numberGreaterThan") + min)
            .max(maxNumberYupLimit, t("pricing:validation.numberLowerThan") + maxNumberYupLimit),
        maxUnits: yup.number()
            .nullable()
            .min(0, t("pricing:validation.positiveNumber"))
            .integer()
            .max(maxNumberYupLimit, t("pricing:validation.numberLowerThan") + maxNumberYupLimit),
        chargeType: yup
            .string()
            .nullable()
            .required(t("translation:validation.fieldMandatory")),
        chargingWay: yup
            .string()
            .nullable()
            .required(t("translation:validation.fieldMandatory")),
    });
};

export const buildEntityRequestFromFormValues = (formValues: FormAncillaryModel, everyLegalEntitiesWithoutAll: string[], dealerFilteredOptions: string[]): AncillaryEntityRequest => {
    return {
        chargeType: formValues.chargeType,
        chargingWay: formValues.chargingWay as string,
        currency: formValues.currency as string,
        maxUnits: formValues.maxUnits === 0 ? null : formValues.maxUnits as number,
        note: formValues.note || null,
        reservationFrom: formValues.reservationFrom,
        reservationTo: dayjs(formValues.reservationTo).toISOString(),
        pickupFrom: dayjs(formValues.pickupFrom).toISOString(),
        pickupTo: dayjs(formValues.pickupTo).toISOString(),
        amountMin: formValues?.minimumAmount ? convertToCents(formValues.minimumAmount)  : null,
        amountMax: formValues?.maximumAmount ? convertToCents(formValues.maximumAmount)  : null,
        dealerId: 1,
        // dealerId: formValues.dealer?.code as number,
        tpUnit: formValues.tpUnit,
        nameAncillary: formValues.nameAncillary,
        code: formValues.code,
        description: formValues.description,
        type: formValues.type as string,
        //exceptionListPack: exceptionPack ? exceptionPack : null,
        subtype: formValues.subtype as string,
        segments: fromGenericFeToGenericBe(formValues.segments) as SelectElementModelBE[],
        cardinality: (formValues.cardinality as number),
        sellDesk: formValues.sellDesk,
        passageToCounterNeeded: formValues.passageToCounterNeeded,
        applicationTiming: formValues.applicationTiming === null ? null : formValues.applicationTiming === "PICK UP" ? "PICK_UP" : "DROP_OFF",
        toleranceTime: formValues.toleranceTime,
        // RF-02 (US-SYS-004) - 5 flag ancillary. Interi: null = nessun limite, 0 = disabilita
        flagResellableAtDeskPostRa: formValues.flagResellableAtDeskPostRa,
        flagResellableInSubstitution: formValues.flagResellableInSubstitution,
        flagDiscountApplicable: formValues.flagDiscountApplicable,
        timeToChange: formValues.timeToChange,
        timeToRemove: formValues.timeToRemove,
        additionalField: formValues.additionalField,
        tax: formValues.tax,
        taxCode: typeof formValues.taxCode === 'object' && formValues.taxCode !== null
            ? (formValues.taxCode as unknown as TaxCodeObject).value
            : formValues.taxCode,
        isOneWay: false,
        dealer: dealerFilteredOptions.length === 1 ? dealerFilteredOptions[0] : formValues.dealer?.code as string,
        legalEntity: everyLegalEntitiesWithoutAll.length === 1 ? everyLegalEntitiesWithoutAll[0] : formValues.legalEntity?.code as string
    };
};

export const buildAncillaryForm = (ancillary: AncillaryEntityResponse, t:any, services: any): FormAncillaryModel => {
    return {
        id: ancillary.id,
        note: ancillary.note ? ancillary.note as string : null,
        reservationFrom: ancillary.reservationFrom,
        reservationTo: ancillary.reservationTo,
        pickupFrom: ancillary.pickupFrom,
        pickupTo: ancillary.pickupTo,
        tpUnit: ancillary.tpUnit,
        currency: ancillary.currency,
        minimumAmount: ancillary.amountMin ? i18nConvertNumberWithDecimals(ancillary.amountMin / 100) : null,
        maximumAmount: ancillary.amountMax ? i18nConvertNumberWithDecimals(ancillary.amountMax / 100) : null,
        chargingWay: ancillary.chargingWay || "",
        maxUnits: ancillary.maxUnits,
        chargeType: ancillary.chargeType,
        nameAncillary: ancillary.nameAncillary, // TODO: implementazione multilanguage?
        code: ancillary.code,
        description: ancillary.description,
        type: ancillary.type,
        subtype: ancillary.subtype,
        segments: fromGenericBeToGenericFe(ancillary.segments),
        cardinality: ancillary.cardinality as number,
        sellDesk: ancillary.sellDesk,
        passageToCounterNeeded: ancillary.passageToCounterNeeded,
        applicationTiming: ancillary.applicationTiming === null ? null : ancillary.applicationTiming === "PICK_UP" ? "PICK UP" : "DROP OFF",
        toleranceTime: ancillary.toleranceTime,
        // RF-02 (US-SYS-004) - 5 flag ancillary
        flagResellableAtDeskPostRa: ancillary.flagResellableAtDeskPostRa ?? false,
        flagResellableInSubstitution: ancillary.flagResellableInSubstitution ?? false,
        flagDiscountApplicable: ancillary.flagDiscountApplicable ?? false,
        timeToChange: ancillary.timeToChange ?? null,
        timeToRemove: ancillary.timeToRemove ?? null,
        additionalField: ancillary.additionalField,
        tax: ancillary.tax,
        taxCode: services.find((s: any) => s.code === ancillary.taxCode)?.name || "",
        dealer: {code: ancillary.dealer, label: t('translation:dealers.'.concat(ancillary.dealer))},
        legalEntity: {code: ancillary.legalEntity, label: t('translation:legalEntities.'.concat(ancillary.legalEntity))}
    };
};
